import { UploadFileEntity } from "../gql-types";
import { TFunction } from "i18next";
import {
  countryTranslateKey,
  stateTranslateKey,
  subCategoryTranslateKey,
} from "./constants/translate";
import { PlatformActivityTypes, ResponseReaction } from "./filter/filter-options";

interface ImageKitParserResponse {
  url: string;
  name: string;
  bigCardThumbnailUrl: string;
  smallCardThumbnailMobileUrl: string;
  smallCardThumbnailUrl: string;
  smallProductThumbnailUrl: string;
  bigProductThumbCard: string;
}

export const formatCategories = (categories) => {
  if (!categories) return [];

  return categories?.map((item) => {
    return {
      id: item.id,
      name: item.attributes.name,
      image: ImageKitParser(item?.attributes?.image?.data),
      slug: item.attributes.slug,
    };
  });
};

export const formatAffariyat = (affariyats) => {
  if (!affariyats) return [];

  return affariyats?.map((item) => {
    return {
      name: item?.attributes?.store.data?.attributes?.sub_category?.data?.attributes?.name,
      image: ImageKitParser(item?.attributes?.image?.data),
      storeName: item?.attributes?.store?.data?.attributes?.name,
      subCategoryName:
        item?.attributes?.store.data?.attributes?.sub_category?.data?.attributes?.name?.split(
          " > ",
        )[1],
      categorySlug:
        item?.attributes.store.data.attributes.sub_category.data.attributes.category.data.attributes
          .slug,
      storeSlug: item?.attributes.store.data.attributes.slug,
      storeLogo: item?.attributes.store.data.attributes.mainImage.data.attributes.url,
    };
  });
};

export const formatProductsImages = (products) => {
  if (!products?.data) return [];
  return products?.data?.map((product) => {
    if (product.attributes.url.includes("imagekit")) {
      return {
        ...product,
        image: ImageKitParser(product),
      };
    }
    return {
      ...product,
      image: ImageKitParser(product),
    };
  });
};

const extractImageKitImageName = (url) => {
  const items = url.split("/");
  return items[items.length - 1];
};

// imageKIT parser
export const ImageKitParser = (imageResponse: UploadFileEntity): ImageKitParserResponse => {
  if (!imageResponse) {
    return {} as ImageKitParserResponse;
  }
  const imageUrl = imageResponse.attributes?.url || "";
  const imageName = extractImageKitImageName(imageUrl);
  const baseImageKitUrl = process.env.NEXT_PUBLIC_BASE_IMAGE_KIT_URL;
  const bigCardThumbnailUrl = baseImageKitUrl + "tr:w-500,h-170,q-70/" + imageName;
  const smallCardThumbnailMobileUrl =
    baseImageKitUrl + "tr:c-maintain_ratio,q-70,w-100,h-100/" + imageName;
  const smallCardThumbnailUrl = baseImageKitUrl + "tr:w-300,h-200/" + imageName;
  const smallProductThumbnailUrl = baseImageKitUrl + "tr:w-100,h-100,c-maintain_ratio/" + imageName;
  const bigProductThumbCard = baseImageKitUrl + "tr:w-349,h-450,c-maintain_ratio/" + imageName;
  return {
    url: imageUrl,
    name: imageName,
    bigCardThumbnailUrl,
    smallCardThumbnailMobileUrl,
    smallCardThumbnailUrl,
    smallProductThumbnailUrl,
    bigProductThumbCard,
  };
};

export const formatCommentsImages = (comments: any[]) => {
  if (!comments.length) {
    return [];
  }
  const formattedComments: any[] = [];
  for (const comment of comments) {
    const formattedImages: any[] = [];

    const { data: images } = comment.attributes.image || {};

    for (const image of images) {
      formattedImages.push({
        attributes: ImageKitParser(image),
      });
    }

    formattedComments.push({
      ...comment,
      formattedImages,
    });
  }
  return formattedComments;
};

export function truncate(str: string): string {
  if (str && str.length > 23) {
    return [str.slice(0, 21), "..."].join("");
  }
  return str || "";
}

export function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  const firstLetter = str.at(0)?.toUpperCase();
  return firstLetter + str.toLowerCase().slice(1);
}

export function formatSubCategoryName(
  subCategoryName: string | undefined,
  translate: TFunction,
): string {
  if (!subCategoryName) return "";

  const translatedText = translate(subCategoryTranslateKey(subCategoryName));
  const words = translatedText.split(" ");

  if (words.length === 0) return translatedText;

  const firstWord = capitalizeFirstLetter(words[0]);
  const remainingWords = words.slice(1).join(" ");

  return remainingWords ? `${firstWord} ${remainingWords}` : firstWord;
}

export const prettifyData = (
  key: string,
  value: any,
  translate: TFunction,
  language: string = "",
) => {
  switch (key) {
    case "likes":
      return `${language === "ar" ? likesFormatter(value) + "+ " : "+" + likesFormatter(value) + " "}${translate("followers")}`;
    case "reviewRate":
      return rateFormatter(value);
    case "hasDelivery":
      return translate(deliveryFormatter(value));
    case "answerSpeed":
      return translate(answerSpeedFormatter(value));
    case "activity":
      return activityFormatter(value);
    case "state":
      return stateFormatter(value, translate, language);
    case "phone":
      return phoneFormatter(value);
    case "website":
      return websiteFormatter(value);
    default:
      return value;
  }
};

export function likesFormatter(value: number) {
  const abs = Math.abs(value);

  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value;
}

export function rateFormatter(rate: string) {
  if (rate === undefined) {
    return;
  }
  return (rate && rate.split("_")[1]) || "";
}

function deliveryFormatter(hasDelivery: boolean | undefined) {
  return hasDelivery ? "deliveryOption" : "";
}

function answerSpeedFormatter(value: string) {
  switch (value) {
    case ResponseReaction.FAST:
      return "answerSpeed.fast";
    case ResponseReaction.MEDIUM:
      return "answerSpeed.medium";
    case ResponseReaction.SLOW:
      return "answerSpeed.slow";
    default:
      return value;
  }
}

function activityFormatter(value: string) {
  switch (value) {
    case PlatformActivityTypes.HIGH:
      return "activity.active";
    case PlatformActivityTypes.MEDIUM:
      return "activity.active";
    case PlatformActivityTypes.LOW:
      return "activity.inactive";
    default:
      return value;
  }
}

function stateFormatter(value: string[], translate: TFunction, language: string) {
  if (value?.length !== 2) {
    return "";
  }
  const country = value[0];
  const state = value[1];
  let array = [
    country && translate(countryTranslateKey(country)),
    state && translate(stateTranslateKey(state)),
  ]?.filter(Boolean);

  return language === "ar" ? array.reverse().join("، ") : array.join(", ");
}

function phoneFormatter(value: string) {
  if (!value) {
    return "";
  }
  return value.startsWith("+216") ? value : `+216 ${value}`;
}

function websiteFormatter(value: string) {
  if (!value) {
    return "";
  }
  return value.startsWith("https://") ? value : `https://${value}`;
}

export function getImageName(value: any) {
  return value?.split("/").slice(-1).pop();
}

export function checkPhoneNumber(value: string) {
  if (!value) return;
  return value.length - 4 === 8;
}

export function isValidUrl(value: string): boolean {
  const url = value.startsWith("https://") ? value : `https://${value}`;
  const urlPattern = /^(https:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/;
  return urlPattern.test(url);
}

//format searchbar stores
export function formatStores(stores) {
  const formattedeStores = stores?.data?.map((newStore) => {
    const store = newStore.attributes || newStore;

    return {
      id: newStore.id,
      name: store.name,
      likesNbr: store.likesNbr,
      imageUrl: store.mainImage.data.attributes.url || store.imageUrl,
      platform: store.platform,
      reviewRate: store.reviewRate,
      slug: store.slug,
    };
  });
  return formattedeStores || [];
}
